import { Reducer } from 'react';
import { v4 as uuidv4 } from 'uuid';
import {
  AcquiringChannel,
  Address,
  ApplicationAction,
  ApplicationActionType,
  PaymentMethodCode,
  PriceType,
  SolutionType,
} from '../../types';
import { Solution } from '../../types/application/opportunity';
import { Application } from '../../types/application/application';
import { PageName as OrderPageName } from '../../../order/pages/page-names';
import { PaymentStatus } from '../../types/application/payment';
import {
  DEVICE_NAMES,
  PRODUCT_CATEGORY_NAME_MAP,
  productCategoryNameMap,
} from 'src/features/order/tech-debt/product-details-extra';

import {
  deviceActivationSteps,
  getDeliveriesAdjustedToMatchTotalSolutionQuantities,
  getMaximumPaymentNumberOfTries,
  getSelectablePaymentMethods,
  getStateAfterNavigateToNextStep,
  getStateAfterNavigateToPreviousStep,
  getStateAfterNextQuickEditStep,
  getStateAfterOnboardingJourneyStarted,
  getStateAfterRemovingAssignDevice,
  getStateWithPendingAppSteps,
  getStateWithStepEnabled,
  isPaymentSessionActive,
  orderJourneySteps,
} from './application-reducer-helpers';
import {
  getCatalogueItemFromCatalogue,
  getPriceType,
  isECommerceInCatalogue,
  isVirtualDevice,
  requiresPayment,
} from '../../helpers/get-device-from-catalogue';
import { PageName as OnboardingPageName } from '../../../onboarding/pages/page-names';
import { CompanyType } from '../../types/application/business';
import { assignDevicesRoute } from 'src/features/order/helpers/routes';
import { Environment } from 'src/Environment';
import { CartItem } from 'src/features/common/types/application/cart';
import { AppDelivery } from 'src/features/common/types/application/delivery';
import {
  updateDeliveryArrays,
  updateDeliveryArraysV2,
} from 'src/features/dpo/pages/assign-devices/helpers';
import { DeliveryOptions } from 'src/features/common/types/application/delivery-options';
import { moveDevicesToNewAddress } from 'src/features/order/helpers/delivery-helpers';
import { setPaymentMethod } from '../../helpers';
import {
  calculateTotalCartQuantity,
  doesCartContainOnceOffItems,
  getTenderTypes,
  isECommerceItemInCart,
  isVirtualOnlyCart,
  normalizeQuantity,
} from '@vfs-digital-channels/ns-utils';
import { getCardFee, getFees, getFeesDPO } from '../../helpers/card-fees-helpers';
import { Collection } from '../../types/application/collection';
import { MAX_NUMBER_OF_TRIES } from 'src/common';
import {
  OzowMethodIcon,
  QRScanToPayIcon,
  StandardPaymentsIcon,
  VISAMasterCardIcon,
} from 'src/features/order';

export const applicationReducer: Reducer<Application, ApplicationAction> = (
  state: Application,
  action: ApplicationAction,
) => {
  switch (action.type) {
    case ApplicationActionType.MULTIPLE_ACTIONS: {
      return action.actions.reduce((stateNew, actionInner) => {
        return processAction(stateNew, actionInner);
      }, state);
    }
    default:
      return processAction(state, action);
  }
};

function processAction(state: Application, action: ApplicationAction) {
  switch (action.type) {
    case ApplicationActionType.START_NEW: {
      const newOrderJourneySteps = [...orderJourneySteps];

      if (action.brokerLogin || action.agentJourney) {
        newOrderJourneySteps.splice(3, 0, {
          pageName: OrderPageName.ASSIGN_DEVICES,
          route: assignDevicesRoute,
          disabled: false,
          groupName: OrderPageName.ASSIGN_DEVICES,
        });
      }

      if (action.agentJourney) {
        const yourDetailsPage = newOrderJourneySteps.find(
          (page) => page.pageName === OrderPageName.YOUR_DETAILS,
        );

        if (yourDetailsPage?.pageName) yourDetailsPage.pageName = OrderPageName.CUSTOMER_DETAILS;
      }

      const newState: Application = {
        solutionType: action.solutionType,
        currentStepIndex: 0,
        steps: newOrderJourneySteps,
        payment: {
          paymentStatus: PaymentStatus.NONE,
          maxNumberOfTries: MAX_NUMBER_OF_TRIES,
        },
        creditVetting: {
          required: false, // -> Journey specific
        },
        brokerLogin: action.brokerLogin,
        agentJourney: action.agentJourney,
        agentId: action.agentId,
        highlightedAddress: action.highlightedAddress,
        remotePaymentsEnabled: action.remotePaymentsEnabled,
        dpoEnabled: action.dpoEnabled,
      };

      if (action.solutionType === SolutionType.buyInStoreFicaOnline && !action.agentJourney) {
        return getStateWithStepEnabled(OrderPageName.ORDER_SUMMARY, false, newState);
      }

      if (action.agentJourney && action.solutionType === SolutionType.buyInStoreFicaOnline) {
        return getStateWithStepEnabled(OrderPageName.FICA, true, newState);
      }

      return newState;
    }

    case ApplicationActionType.RESTART: {
      const newOrderJourneySteps = [...orderJourneySteps];

      if (state.brokerLogin) {
        newOrderJourneySteps.splice(3, 0, {
          pageName: OrderPageName.ASSIGN_DEVICES,
          route: assignDevicesRoute,
          disabled: true,
          groupName: OrderPageName.ASSIGN_DEVICES,
        });
      }

      const newState: Application = {
        ...state,
        solutionType: SolutionType.buyOrRentOnlineFicaOnline,
        currentStepIndex: 0,
        steps: newOrderJourneySteps,
        payment: {
          paymentStatus: PaymentStatus.NONE,
          maxNumberOfTries: MAX_NUMBER_OF_TRIES,
        },
        creditVetting: {
          required: false,
        },
        eCommerceRequired: false,
        stage: {
          ...state.stage,
          isOrderComplete: false,
        },
      };
      return newState;
    }

    case ApplicationActionType.UPDATE_SOLUTION_TYPE:
      if (action.solutionType === SolutionType.buyInStoreFicaOnline) {
        state = getStateWithStepEnabled(OrderPageName.ORDER_SUMMARY, false, state);
      }

      return {
        ...state,
        solutionType: action.solutionType,
      };

    case ApplicationActionType.SET_MERCHANT_AGREEMENT_UPLOADED:
      return {
        ...state,
        isMerchantAgreementUploaded: action.isMerchantAgreementUploaded,
      };

    case ApplicationActionType.SET_MERCHANT_AGREEMENT: {
      const newState: Application = {
        ...state,
        merchantAgreementScreenshot: action.merchantAgreementScreenshot,
      };

      return newState;
    }

    case ApplicationActionType.SET_SIGNED_MERCHANT_AGREEMENT: {
      const newState: Application = {
        ...state,
        signedMerchantAgreement: action.signedMerchantAgreement,
      };

      return newState;
    }

    case ApplicationActionType.SET_TERMS_AND_CONDITIONS: {
      const newState: Application = {
        ...state,
        termsAndConditions: action.termsAndConditions,
      };

      return newState;
    }

    case ApplicationActionType.DEVICE_ACTIVATION:
      const app: Application = {
        ...state,
        deviceActivationJourney: true,
        deviceActivationTagging: true,
        dpoEnabled: action.dpoEnabled,
        solutionType: SolutionType.buyInStoreFicaOnline,
        currentStepIndex: 0,
        tapOnPhoneJourney: false,
        steps: deviceActivationSteps,
        useStockOnHandDeliveryOption: true,
      };
      return app;

    case ApplicationActionType.UPDATE_CARD_MACHINE:
      return {
        ...state,
        cardMachines: action.cardMachines,
      };

    case ApplicationActionType.UPDATE_THIRD_PARTY_DEFAULT_ADDRESS_SOLUTIONS:
      return {
        ...state,
        thirdPartyDefaultAddressSolutions: action.thirdPartyDefaultAddressSolutions,
      };

    case ApplicationActionType.START_TAP_ON_PHONE_JOURNEY:
      const newState: Application = {
        ...state,
        solutionType: SolutionType.buyOrRentOnlineFicaOnline,
        currentStepIndex: 0,
        steps: orderJourneySteps,
        payment: {
          paymentStatus: PaymentStatus.NONE,
          maxNumberOfTries: MAX_NUMBER_OF_TRIES,
        },
        creditVetting: {
          required: action.creditVettingRequired,
        },
        passportRequired: action.passportEnabled,
        tapOnPhoneJourney: true,
        dpoEnabled: action.dpoEnabled,
      };

      return getStateWithStepEnabled(OrderPageName.CHOOSE_SOLUTION, false, newState);

    case ApplicationActionType.UPDATE_BASKET_QUANTITY: {
      const actionCatalogueItem = getCatalogueItemFromCatalogue(
        action.catalogue,
        action.productId,
        action.acquiringChannel,
      );

      if (!actionCatalogueItem) {
        throw new Error(`App reducer revamp: unknown product: product id: ${action.productId}`);
      }

      const solutionNew: Solution = {
        productId: action.productId,
        acquiringChannel: action.acquiringChannel,
        quantity: action.quantity,
      };

      let solutionNewAdded = false;

      const solutionsUpdated = (state.opportunity?.solutions || []).map((solution) => {
        if (
          solution.productId === solutionNew.productId &&
          solution.acquiringChannel === solutionNew.acquiringChannel
        ) {
          solutionNewAdded = true;
          return solutionNew;
        } else {
          return solution;
        }
      });

      if (!solutionNewAdded) {
        solutionsUpdated.push(solutionNew);
      }

      const newState = {
        ...state,
        opportunity: {
          ...state.opportunity,
          solutions: solutionsUpdated,
          deliveries: getDeliveriesAdjustedToMatchTotalSolutionQuantities(
            solutionsUpdated,
            state.opportunity?.deliveries || [],
          ),
        },
      };

      // 3PO Broker payment
      if (
        !!state.brokerLogin &&
        (actionCatalogueItem.device.title === DEVICE_NAMES.VODAPAY_KWIKA ||
          actionCatalogueItem.device.title === DEVICE_NAMES.MAX_2)
      ) {
        state = getStateWithStepEnabled(OrderPageName.PAY, false, state);

        state = getStateWithStepEnabled(OrderPageName.PAY_ONLINE, true, state);
        state = getStateWithStepEnabled(OrderPageName.PAY_STATUS, true, state);

        newState.steps = [...(state.steps || [])];
      }

      const thirdPartyCreditVetting =
        (!!state.brokerLogin || state.agentJourney) &&
        actionCatalogueItem.price.priceType === PriceType.MONTHLY;

      if (
        !Environment.current?.disabledFeatures?.creditVetting &&
        (actionCatalogueItem.device.acquiringChannel === AcquiringChannel.rentOnlineFicaOnline ||
          thirdPartyCreditVetting)
      ) {
        newState.creditVetting = {
          ...state.creditVetting,
          required: action.quantity > 0,
        };
      }

      if (isECommerceInCatalogue(action.catalogue, solutionsUpdated)) {
        newState.eCommerceRequired = action.quantity > 0;
        return getStateWithStepEnabled(
          Environment.current.disabledFeatures.revamp
            ? OrderPageName.PAYMENT_TYPE
            : OrderPageName.PAYMENT_TYPE_REVAMP,
          action.quantity > 0,
          newState,
        );
      }

      return newState;
    }

    case ApplicationActionType.UPDATE_DELIVERY_OPTIONS: {
      const { isDelivery, device } = action;

      // Create copies of arrays to avoid mutation
      let deliveries_ = [...(state.deliveries || [])];
      let collections_ = [...(state.collections || [])];

      const { deliveries, collections } = !Environment.current.disabledFeatures.splitDeliveries
        ? updateDeliveryArrays(deliveries_, collections_, device, isDelivery)
        : updateDeliveryArraysV2(deliveries_, collections_, device, isDelivery);

      const totalScanned = (state.cardMachines || []).length;
      const totalCollections = collections.reduce(
        (total, collection) => total + collection.quantity,
        0,
      );

      if (isDelivery && Environment.current.disabledFeatures.splitDeliveries) {
        const newCardMachines = (state.cardMachines ?? []).filter(
          (cardMachine) => cardMachine.productId !== device.productId,
        );

        const app: Application = {
          ...state,
          cardMachines: newCardMachines,
          deliveries,
          collections,
        };
        return app;
      }

      const isInCardMachines = (state.cardMachines || []).find(
        (collection) =>
          collection.productId === device.productId && collection.addressId === device.addressId,
      );

      // Remove device from card machines if it is toggled from collection to delivery
      if (isDelivery && isInCardMachines && totalScanned !== totalCollections) {
        const index = (state.cardMachines || []).indexOf(isInCardMachines);

        if (index > -1) {
          const newCardMachines = (state.cardMachines || []).toSpliced(index, 1);
          const app: Application = {
            ...state,
            cardMachines: newCardMachines,
            deliveries,
            collections,
          };
          return app;
        }
      }

      return {
        ...state,
        deliveries,
        collections,
      };
    }

    case ApplicationActionType.UPDATE_CART_PAYMENT_TYPE: {
      return {
        ...state,
        cart: action.cart || [],
      };
    }

    case ApplicationActionType.UPDATE_CART_QUANTITY: {
      const actionCatalogueItem = getCatalogueItemFromCatalogue(
        action.catalogue,
        action.productId,
        action.acquiringChannel,
      );

      if (!actionCatalogueItem) {
        throw new Error(`App reducer revamp: unknown product: product id: ${action.productId}`);
      }

      let updatedCollections = state.collections ? [...state.collections] : [];
      let updatedDeliveries = state.deliveries ? [...state.deliveries] : [];
      const isRental =
        action.paymentMethodCode && action.paymentMethodCode === PaymentMethodCode.RENTAL;
      const priceType = isRental
        ? PriceType.MONTHLY
        : getPriceType(action.paymentMethodCode as PaymentMethodCode);

      const isDeviceActivation = !!state.deviceActivationJourney;
      const isRemotePaymentEnabled =
        !!state.brokerLogin || state.agentJourney ? state.remotePaymentsEnabled : true;
      const deviceDeliveryOptions = actionCatalogueItem.device.deliveryOptions || [];
      const rentalDeliveryOptions = isRental ? [DeliveryOptions.Deliver] : [];
      const defaultDeliveryOptions =
        isDeviceActivation || !isRemotePaymentEnabled
          ? [DeliveryOptions.Stock]
          : deviceDeliveryOptions;
      const deliveryOptions = isRental ? rentalDeliveryOptions : defaultDeliveryOptions;
      const oldCartQuantity =
        (state.cart || []).find(
          (item) =>
            item.productId === action.productId &&
            item.acquiringChannel === action.acquiringChannel,
        )?.quantity || 0;

      const newCartItem: CartItem = {
        id: action.cartItemId || uuidv4(),
        productId: action.productId,
        acquiringChannel: action.acquiringChannel,
        title: actionCatalogueItem.device.title,
        quantity: action.quantity,
        isVirtual: actionCatalogueItem.device.isVirtual || false,
        price: isRental ? actionCatalogueItem.price.rentalPrice : actionCatalogueItem.price.price,
        tagline: actionCatalogueItem.device.tagline,
        priceType: priceType,
        isTenderType: actionCatalogueItem.device.isTenderType,
        deliveryOptions: deliveryOptions,
        image: actionCatalogueItem.device.imageBase64 || '',
        paymentMethodCode: action.paymentMethodCode,
        productCategoryName: actionCatalogueItem.device.productCategoryName,
      };

      let newCartItemAdded = false;

      const updatedCartItems = (state.cart || []).map((item) => {
        if (
          item.productId === newCartItem.productId &&
          item.acquiringChannel === newCartItem.acquiringChannel &&
          item.id === newCartItem.id
        ) {
          newCartItemAdded = true;
          return newCartItem;
        }
        return item;
      });

      // Find the index of the item in the deliveries array
      const defaultDeliveryIndex = updatedDeliveries.findIndex(
        (delivery) =>
          delivery.productId === action.productId &&
          delivery.addressId === 'default' &&
          delivery.cartItemId === action.cartItemId,
      );

      let assignedToDeliveries = false;

      if (
        !isDeviceActivation &&
        (isRemotePaymentEnabled || isRental) &&
        defaultDeliveryIndex === -1 &&
        newCartItem.deliveryOptions.includes(DeliveryOptions.Deliver) &&
        !isVirtualDevice(newCartItem.title) &&
        action.quantity > oldCartQuantity
      ) {
        // If the item doesn't exist and has more than one delivery option, add it
        updatedDeliveries.push({
          id: uuidv4(),
          productId: action.productId,
          addressId: 'default',
          quantity: 1,
          cartItemId: newCartItem.id,
          priceType: newCartItem.priceType as PriceType,
        });
        assignedToDeliveries = true;
      }

      // If the item exists, update its quantity
      if (
        defaultDeliveryIndex !== -1 &&
        newCartItem.deliveryOptions.includes(DeliveryOptions.Deliver) &&
        !isVirtualDevice(newCartItem.title) &&
        action.quantity !== oldCartQuantity
      ) {
        const quantityDifference = normalizeQuantity(
          action.productId,
          action.quantity,
          state.deliveries || [],
          state.collections || [],
        );
        updatedDeliveries[defaultDeliveryIndex].quantity = quantityDifference;
        assignedToDeliveries = updatedDeliveries[defaultDeliveryIndex].quantity >= 0;
      }

      // if it's broker or agent journey, update collections arrays
      if (
        !!state.brokerLogin ||
        state.agentJourney ||
        isDeviceActivation ||
        !isRemotePaymentEnabled
      ) {
        const defaultCollectionIndex = updatedCollections.findIndex(
          (collection) =>
            collection.productId === action.productId &&
            collection.addressId === 'default' &&
            collection.cartItemId === action.cartItemId,
        );

        if (
          defaultCollectionIndex !== -1 &&
          !assignedToDeliveries &&
          !isVirtualDevice(newCartItem.title)
        ) {
          // If the item exists, update its quantity
          updatedCollections[defaultCollectionIndex].quantity +=
            action.quantity > oldCartQuantity ? 1 : -1;
          assignedToDeliveries = true;
        }

        if (
          newCartItem.deliveryOptions.length === 1 &&
          newCartItem.deliveryOptions.includes(DeliveryOptions.Stock) &&
          !assignedToDeliveries &&
          !isVirtualDevice(newCartItem.title)
        ) {
          // If the item doesn't exist and has only one delivery option, add it
          updatedCollections.push({
            id: uuidv4(),
            productId: action.productId,
            addressId: 'default',
            quantity: isDeviceActivation ? newCartItem.quantity : 1,
            cartItemId: newCartItem.id,
            priceType: newCartItem.priceType as PriceType,
          });
        }
      }

      if (!newCartItemAdded) {
        updatedCartItems.push(newCartItem);
      }

      let newState: Application = {
        ...state,
        cart: updatedCartItems,
        deliveries: updatedDeliveries,
        collections: updatedCollections,
      };

      if (isECommerceItemInCart(newState.cart || [])) {
        newState.eCommerceRequired = action.quantity > 0;
        newState = getStateWithStepEnabled(
          Environment.current.disabledFeatures.revamp
            ? OrderPageName.PAYMENT_TYPE
            : OrderPageName.PAYMENT_TYPE_REVAMP,
          action.quantity > 0,
          newState,
          action.quantity === 0,
        );
      }

      if (
        !Environment.current?.disabledFeatures?.creditVetting &&
        (newCartItem.acquiringChannel === AcquiringChannel.rentOnlineFicaOnline ||
          newCartItem.paymentMethodCode === PaymentMethodCode.RENTAL)
      ) {
        newState.creditVetting = {
          ...state.creditVetting,
          required: action.quantity > 0,
        };
      }

      const isThirdParty = !!state.brokerLogin || state.agentJourney;
      const hasOnceOffItems = doesCartContainOnceOffItems(newState.cart || []);

      if (isThirdParty) {
        if (!hasOnceOffItems) {
          newState.selectedPaymentMethodCode = undefined;
        }

        newState = getStateWithStepEnabled(OrderPageName.PAY, !hasOnceOffItems, newState);
        newState = getStateWithStepEnabled(OrderPageName.PAY_ONLINE, hasOnceOffItems, newState);
        newState = getStateWithStepEnabled(OrderPageName.PAY_STATUS, hasOnceOffItems, newState);
        return getStateAfterRemovingAssignDevice(
          OrderPageName.ASSIGN_DEVICES,
          !isVirtualOnlyCart(newState.cart || []),
          newState,
        );
      }

      if (!Environment.current.disabledFeatures.vodapayGateway && !isThirdParty) {
        newState = getStateWithStepEnabled(
          OrderPageName.PROCESS_PAYMENT,
          hasOnceOffItems,
          newState,
        );
        newState = getStateWithStepEnabled(OrderPageName.PAY_STATUS, hasOnceOffItems, newState);
      }

      return newState;
    }

    case ApplicationActionType.REMOVE_CART_QUANTITY: {
      const actionCatalogueItem = getCatalogueItemFromCatalogue(
        action.catalogue,
        action.productId,
        action.acquiringChannel,
      );

      if (!actionCatalogueItem) {
        throw new Error(`App reducer revamp: unknown product: product id: ${action.productId}`);
      }

      let updatedCollections = state.collections ? [...state.collections] : [];
      let updatedDeliveries = state.deliveries ? [...state.deliveries] : [];
      const removedCartItem = (state.cart ?? []).find(
        (cartItem) => cartItem.productId === action.productId && cartItem.id === action.cartItemId,
      );
      const productCategoryName = productCategoryNameMap.get(actionCatalogueItem.device.title);

      let cartItemsAfterRemove = (state.cart || []).filter((item) => {
        const cartCatalogueItem = getCatalogueItemFromCatalogue(
          action.catalogue,
          item.productId,
          action.acquiringChannel,
        );

        if (!cartCatalogueItem) {
          throw new Error(`App reducer revamp: unknown product: ${item.productId}`);
        }

        return !(
          item.productId === action.productId &&
          item.id === action.cartItemId &&
          item.acquiringChannel === action.acquiringChannel
        );
      });

      // If its a ProductCategory remove its tenderTypes
      if (!!productCategoryName) {
        cartItemsAfterRemove = cartItemsAfterRemove.filter((item) => {
          const cartCatalogueItem = getCatalogueItemFromCatalogue(
            action.catalogue,
            item.productId,
            action.acquiringChannel,
          );

          if (!cartCatalogueItem) {
            throw new Error(`App reducer revamp: unknown product: ${item.productId}`);
          }
          return !(cartCatalogueItem.device.productCategoryName === productCategoryName);
        });
      }

      // Remove the product/item from the deliveries array
      updatedDeliveries = updatedDeliveries.filter(
        (delivery) =>
          !(delivery.productId === action.productId && delivery.cartItemId === action.cartItemId),
      );

      // if it's broker or agent journey, remove the item from collections array
      if (!!state.brokerLogin || state.agentJourney) {
        updatedCollections = updatedCollections.filter(
          (collection) =>
            !(
              collection.productId === action.productId &&
              collection.cartItemId === action.cartItemId
            ),
        );
      }

      let newState: Application = {
        ...state,
        cart: cartItemsAfterRemove,
        deliveries: updatedDeliveries,
        collections: updatedCollections,
      };

      if (!isECommerceItemInCart(newState.cart || [])) {
        newState.eCommerceRequired = isECommerceItemInCart(newState.cart || []);
        newState = getStateWithStepEnabled(
          Environment.current.disabledFeatures.revamp
            ? OrderPageName.PAYMENT_TYPE
            : OrderPageName.PAYMENT_TYPE_REVAMP,
          isECommerceItemInCart(newState.cart || []),
          newState,
        );
      }

      if (
        !Environment.current?.disabledFeatures?.creditVetting &&
        removedCartItem &&
        (removedCartItem.acquiringChannel === AcquiringChannel.rentOnlineFicaOnline ||
          removedCartItem.paymentMethodCode === PaymentMethodCode.RENTAL)
      ) {
        newState.creditVetting = {
          ...state.creditVetting,
          required: false,
        };
      }

      const hasOnceOffItems = doesCartContainOnceOffItems(newState.cart || []);
      const isThirdParty = !!state.brokerLogin || state.agentJourney;

      if (isThirdParty) {
        newState = getStateWithStepEnabled(OrderPageName.PAY, !hasOnceOffItems, newState);
        newState = getStateWithStepEnabled(OrderPageName.PAY_ONLINE, hasOnceOffItems, newState);
        newState = getStateWithStepEnabled(OrderPageName.PAY_STATUS, hasOnceOffItems, newState);
        return getStateAfterRemovingAssignDevice(
          OrderPageName.ASSIGN_DEVICES,
          !isVirtualOnlyCart(newState.cart || []),
          newState,
        );
      }

      if (!Environment.current.disabledFeatures.vodapayGateway && !isThirdParty) {
        newState = getStateWithStepEnabled(
          OrderPageName.PROCESS_PAYMENT,
          hasOnceOffItems,
          newState,
        );
        newState = getStateWithStepEnabled(OrderPageName.PAY_STATUS, hasOnceOffItems, newState);
      }

      return newState;
    }

    case ApplicationActionType.DELETE_ADDRESS:
      const deviceAssignedToAddressToDelete = (state.deliveries ?? []).filter(
        (device) => device.addressId === action.addressId,
      );
      const addresses = state.addresses?.filter((address) => address.id !== action.addressId);

      const deliveries = moveDevicesToNewAddress(
        state.deliveries || [],
        deviceAssignedToAddressToDelete,
        'default',
        action.addressId,
      );

      // If it is third party include collections
      if (!!state.brokerLogin || state.agentJourney) {
        const deviceAssignedToAddressToDeleteCollect = (state.collections ?? []).filter(
          (device) => device.addressId === action.addressId,
        );

        const collections = moveDevicesToNewAddress(
          state.collections || [],
          deviceAssignedToAddressToDeleteCollect,
          'default',
          action.addressId,
        );

        return {
          ...state,
          addresses,
          collections,
          deliveries,
        };
      }

      return {
        ...state,
        addresses,
        deliveries,
      };

    case ApplicationActionType.ADD_ADDRESS: {
      const newAddress: Address = {
        id: action.address.id ?? 'default',
        street: action.address.street,
        building: action.address.building,
        suburb: action.address.suburb,
        city: action.address.city,
        province: action.address.province,
        country: action.address.country ?? 'South Africa',
        postalCode: action.address.postalCode,
        recipient: action.recipient,
      };

      return {
        ...state,
        addresses: [...(state.addresses || []), newAddress],
      };
    }

    case ApplicationActionType.UPDATE_ADDRESS:
      if (!action.address.id) return state;

      const updatedAddress = (state.addresses || []).map((address) => {
        if (address.id === action.address.id)
          return {
            ...action.address,
            recipient: action.recipient,
          };

        return address;
      });

      return {
        ...state,
        addresses: updatedAddress,
      };

    case ApplicationActionType.ADD_DELIVERY_ADDRESS: {
      const newAddress: Address = {
        id: action.address.id ?? 'default',
        street: action.address.street,
        building: action.address.building,
        suburb: action.address.suburb,
        city: action.address.city,
        province: action.address.province,
        country: action.address.country ?? 'South Africa',
        postalCode: action.address.postalCode,
      };

      let addressUpdated = false;

      const newAddresses = (state?.addresses ?? []).map((address) => {
        if (address.id === newAddress.id) {
          addressUpdated = true;
          return newAddress;
        }
        return address;
      });

      if (!addressUpdated) {
        newAddresses.push(newAddress);
      }

      const updatedStateDeliveries: AppDelivery[] = (state?.deliveries ?? []).map((delivery) => {
        const product = action.products.find((product) => product.id === delivery.productId);
        if (product) {
          return { ...delivery, quantity: delivery.quantity - product.quantity };
        }
        return delivery;
      });

      const deliveries: AppDelivery[] = action.products.map(
        ({ id, quantity, cartItemId, priceType }) => ({
          id: uuidv4(),
          productId: id,
          addressId: newAddress.id ?? 'default',
          quantity,
          cartItemId,
          priceType,
        }),
      );

      const newState: Application = {
        ...state,
        addresses: newAddresses,
        deliveries: [...updatedStateDeliveries, ...deliveries],
      };
      return newState;
    }

    case ApplicationActionType.UPDATE_DELIVERY_ADDRESS: {
      const newAddress: Address = {
        id: action.address.id ?? 'default',
        street: action.address.street,
        building: action.address.building,
        suburb: action.address.suburb,
        city: action.address.city,
        province: action.address.province,
        country: action.address.country ?? 'South Africa',
        postalCode: action.address.postalCode,
      };

      let addressUpdated = false;

      const newAddresses = (state?.addresses ?? []).map((address) => {
        if (address.id === newAddress.id) {
          addressUpdated = true;
          return newAddress;
        }
        return address;
      });

      if (!addressUpdated) {
        newAddresses.push(newAddress);
      }

      const updatedDeliveries = (state.deliveries ?? []).map((delivery) => {
        if (delivery.addressId === newAddress.id) {
          const updatedProduct = action.products.find(
            (product) => product.id === delivery.productId,
          );
          if (updatedProduct) {
            return { ...delivery, quantity: updatedProduct.quantity };
          }
        }
        return delivery;
      });

      // find all deliveries for a product
      action.products.forEach((product) => {
        // calculate the total number of products that are not being delivered to the default address
        const number = updatedDeliveries.reduce((total, delivery) => {
          if (delivery.addressId !== 'default' && delivery.productId === product.id) {
            return total + delivery.quantity;
          }
          return total + 0;
        }, 0);

        const defaultDeliveryIndex = updatedDeliveries.findIndex(
          (delivery) => delivery.addressId === 'default' && delivery.productId == product.id,
        );

        const productCart = (state.cart ?? []).find((item) => item.productId === product.id);

        if (productCart) {
          updatedDeliveries[defaultDeliveryIndex].quantity = productCart.quantity - number;
        }
      });

      const newState: Application = {
        ...state,
        addresses: newAddresses,
        deliveries: updatedDeliveries,
      };
      return newState;
    }

    case ApplicationActionType.UPDATE_DEVICE_DELIVERIES:
      return {
        ...state,
        deliveries: action.deliveries,
      };

    case ApplicationActionType.UPDATE_DEVICE_COLLECTIONS:
      return {
        ...state,
        collections: action.collections,
      };

    case ApplicationActionType.REMOVE_BASKET_QUANTITY: {
      const actionCatalogueItem = getCatalogueItemFromCatalogue(
        action.catalogue,
        action.productId,
        action.acquiringChannel,
      );

      if (!actionCatalogueItem) {
        throw new Error(`App reducer revamp: unknown product: product id: ${action.productId}`);
      }

      const solutionsAfterRemove = (state.opportunity?.solutions || []).filter((solution) => {
        const solutionCatalogueItem = getCatalogueItemFromCatalogue(
          action.catalogue,
          solution.productId,
          action.acquiringChannel,
        );

        if (!solutionCatalogueItem) {
          throw new Error(`App reducer revamp: unknown product: ${solution.productId}`);
        }

        return !(
          (solution.productId === action.productId &&
            solution.acquiringChannel === action.acquiringChannel) ||
          (actionCatalogueItem.device.productCategoryName &&
            actionCatalogueItem.device.productCategoryName ===
              solutionCatalogueItem.device.productCategoryName)
        );
      });

      const newState = {
        ...state,
        opportunity: {
          ...state.opportunity,
          solutions: solutionsAfterRemove,
          deliveries: getDeliveriesAdjustedToMatchTotalSolutionQuantities(
            solutionsAfterRemove,
            state.opportunity?.deliveries || [],
          ),
        },
      };

      if (!!state.brokerLogin && actionCatalogueItem.device.title === DEVICE_NAMES.VODAPAY_KWIKA) {
        state = getStateWithStepEnabled(OrderPageName.PAY, true, state);
        state = getStateWithStepEnabled(OrderPageName.PAY_ONLINE, false, state);
        state = getStateWithStepEnabled(OrderPageName.PAY_STATUS, false, state);
        newState.steps = [...(state.steps || [])];
      }

      const thirdPartyCreditVetting =
        (!!state.brokerLogin || state.agentJourney) &&
        actionCatalogueItem.device.secondaryAcquiringChannel ===
          AcquiringChannel.rentOnlineFicaOnline;

      if (
        !Environment.current?.disabledFeatures?.creditVetting &&
        (actionCatalogueItem.device.acquiringChannel === AcquiringChannel.rentOnlineFicaOnline ||
          thirdPartyCreditVetting)
      ) {
        newState.creditVetting = {
          ...state.creditVetting,
          required: false,
        };
      }

      if (!isECommerceInCatalogue(action.catalogue, solutionsAfterRemove)) {
        newState.eCommerceRequired = isECommerceInCatalogue(action.catalogue, solutionsAfterRemove);
        return getStateWithStepEnabled(
          OrderPageName.PAYMENT_TYPE,
          isECommerceInCatalogue(action.catalogue, solutionsAfterRemove),
          newState,
        );
      }

      return newState;
    }

    case ApplicationActionType.UPDATE_CONTACT_DETAILS:
      // VB POS: Add city to deliveries
      state.opportunity = {
        ...state.opportunity,
        deliveries: state.opportunity?.deliveries?.map((delivery) => {
          if (!delivery.address.city) {
            delivery.address.city = action.contactDetails.businessAddress?.city;
          }

          return delivery;
        }),
      };

      return {
        ...state,
        contact: {
          ...state.contact,
          ...action.contactDetails,
        },
      };

    case ApplicationActionType.UPDATE_REFERENCE_NUMBER:
      return {
        ...state,
        referenceNumber: action.referenceNumber,
      };

    case ApplicationActionType.UPDATE_PAYMENT_TYPE: {
      if (!!state.brokerLogin || state.agentJourney) {
        state = getStateWithStepEnabled(OrderPageName.PAY, !action.onlinePaymentRequired, state);

        state = getStateWithStepEnabled(
          OrderPageName.PAY_ONLINE,
          action.onlinePaymentRequired,
          state,
        );
        state = getStateWithStepEnabled(
          OrderPageName.PAY_STATUS,
          action.onlinePaymentRequired,
          state,
        );

        state.steps = [...(state.steps || [])];
      }

      return {
        ...state,
        payment: {
          ...state.payment,
          paymentType: action.paymentType,
          onlinePaymentRequired: action.onlinePaymentRequired,
        },
      };
    }

    case ApplicationActionType.UPDATE_PAYMENT_STATUS: {
      const newState: Application = {
        ...state,
        payment: {
          ...state.payment,
          paymentStatus: action.paymentStatus,
          orderNumber: action.orderNumber || state.payment?.orderNumber,
        },
      };
      return newState;
    }

    case ApplicationActionType.UPDATE_DOCUMENTS:
      return {
        ...state,
        documents: action.documents,
      };

    case ApplicationActionType.NAVIGATE_TO_NEXT_STEP:
      if (state.quickEdit?.returnIndex !== undefined) {
        if (!state.quickEdit?.steps || state.quickEdit?.steps.length === 0) {
          return {
            ...state,
            currentStepIndex: state.quickEdit.returnIndex,
            quickEdit: {
              returnIndex: undefined,
            },
          };
        }

        return getStateAfterNextQuickEditStep(state.quickEdit.steps, state, true);
      }

      if (
        state.steps &&
        state.currentStepIndex &&
        (state.steps[state.currentStepIndex].pageName === OrderPageName.PAY ||
          state.steps[state.currentStepIndex].pageName === OrderPageName.PAY_STATUS)
      ) {
        return getStateAfterOnboardingJourneyStarted(state);
      }

      return getStateAfterNavigateToNextStep(state);

    case ApplicationActionType.NAVIGATE_TO_PREVIOUS_STEP:
      return getStateAfterNavigateToPreviousStep(state);

    case ApplicationActionType.UPDATE_DIRECTOR_DETAILS:
      return {
        ...state,
        directors: action.directorDetails,
      };

    case ApplicationActionType.UPDATE_DELIVERIES:
      return {
        ...state,
        opportunity: {
          ...state.opportunity,
          deliveries: action.deliveries,
        },
      };

    case ApplicationActionType.SET_DPO: {
      return {
        ...state,
        dpoEnabled: action.dpoEnabled,
      };
    }

    case ApplicationActionType.HIGHLIGHT_ADDRESS:
      return {
        ...state,
        highlightedAddress: action.highlightedAddress,
      };

    case ApplicationActionType.HIGHLIGHT_DELIVERY:
      return {
        ...state,
        highlightedDeliveryId: action.deliveryId,
      };

    case ApplicationActionType.UPDATE_BUSINESS_DETAILS: {
      const newState = {
        ...state,
        businessDetails: action.businessDetails,
      };

      const isSolePropNotSelected = action.businessDetails.companyType !== CompanyType.SOLE_PROP;

      return getStateWithStepEnabled(
        OnboardingPageName.DIRECTOR_DETAILS,
        isSolePropNotSelected,
        newState,
      );
    }

    case ApplicationActionType.UDPATE_RESIDENTIAL_ADDRESS: {
      return {
        ...state,
        contact: {
          ...state.contact,
          residentialAddress: action.address,
          isResidentialAddressSameAsBusinessAddress:
            action.isResidentialAddressSameAsBusinessAddress,
        },
      };
    }
    case ApplicationActionType.UPDATE_BANK_DETAILS:
      return {
        ...state,
        bankDetails: action.bankDetails,
      };

    case ApplicationActionType.UPDATE_CREDIT_VETTING_STATUS:
      return {
        ...state,
        creditVetting: {
          ...state.creditVetting,
          bankAccountValidationStatus: action.bankAccountValidationStatus,
          bankAccountValidationAttempts: action.bankAccountValidationAttempts,
        },
      };

    case ApplicationActionType.KILL_APPLICATION:
      return {
        ...state,
        killApplication: true,
      };

    case ApplicationActionType.QUICK_EDIT: {
      return getStateAfterNextQuickEditStep(action.steps, state, false);
    }

    case ApplicationActionType.SET_CONTINUE_WITH_OTP_APP: {
      let applicationNew = {
        ...action.application,
      };

      if (applicationNew.solutionType === SolutionType.thirdPartyOnboarding && action.dealerId) {
        applicationNew.brokerLogin = action.dealerId;
        applicationNew.remotePaymentsEnabled = action.remotePaymentsEnabled;

        if (
          applicationNew.remotePayment?.transactionId &&
          applicationNew.payment?.orderNumber &&
          isPaymentSessionActive(applicationNew.payment?.orderNumber)
        ) {
          applicationNew.remotePayment.initiated = true;
        }
      }

      if (!Environment.current?.disabledFeatures?.creditVetting) {
        const isCreditVettingEnabled: boolean = (applicationNew.cart ?? []).some(
          (cartItem) =>
            cartItem.paymentMethodCode === PaymentMethodCode.RENTAL ||
            cartItem.acquiringChannel === AcquiringChannel.rentOnlineFicaOnline,
        );

        if (applicationNew?.creditVetting) {
          applicationNew.creditVetting.required = isCreditVettingEnabled;
        }

        applicationNew = getStateWithStepEnabled(
          OnboardingPageName.RES_ADDRESS,
          isCreditVettingEnabled,
          applicationNew,
        );
      }

      const isSolePropNotSelected =
        applicationNew.businessDetails?.companyType !== CompanyType.SOLE_PROP;

      applicationNew = getStateWithStepEnabled(
        OnboardingPageName.DIRECTOR_DETAILS,
        isSolePropNotSelected,
        { ...applicationNew, currentStepIndex: action.application.currentStepIndex },
      );

      const isThirdParty = !!state.brokerLogin || state.agentJourney;
      const hasOnceOffItems = doesCartContainOnceOffItems(applicationNew.cart || []);

      if (applicationNew.pendingApp?.isPending) {
        applicationNew = getStateWithPendingAppSteps(applicationNew);
      }

      if (isThirdParty) {
        applicationNew.isOTPLogin = false;

        if (applicationNew?.stage?.isOrderComplete) return applicationNew;

        applicationNew = getStateWithStepEnabled(
          OrderPageName.PAY,
          !hasOnceOffItems,
          applicationNew,
        );
        applicationNew = getStateWithStepEnabled(
          OrderPageName.PAY_ONLINE,
          hasOnceOffItems,
          applicationNew,
        );
        applicationNew = getStateWithStepEnabled(
          OrderPageName.PAY_STATUS,
          hasOnceOffItems,
          applicationNew,
        );

        return getStateAfterRemovingAssignDevice(
          OrderPageName.ASSIGN_DEVICES,
          !isVirtualOnlyCart(applicationNew.cart || []),
          applicationNew,
        );
      }

      return {
        ...applicationNew,
        isOTPLogin: false,
        payment: {
          paymentStatus: PaymentStatus.PAYMENT_SUCCESSFUL,
        },
      };
    }

    case ApplicationActionType.SET_WHOLE_APP:
      let applicationNew = {
        ...action.application,
      };

      if (applicationNew.deviceActivationJourney) {
        applicationNew = {
          ...applicationNew,
          payment: {
            paymentStatus: PaymentStatus.PAYMENT_SUCCESSFUL,
            maxNumberOfTries: MAX_NUMBER_OF_TRIES,
          },
        };
      }

      if (applicationNew.solutionType === SolutionType.thirdPartyOnboarding && action.dealerId) {
        applicationNew.brokerLogin = action.dealerId;
        applicationNew.remotePaymentsEnabled = action.remotePaymentsEnabled;

        if (
          applicationNew.remotePayment?.transactionId &&
          applicationNew.payment?.orderNumber &&
          isPaymentSessionActive(applicationNew.payment?.orderNumber)
        ) {
          applicationNew.remotePayment.initiated = true;
        }

        // 3PO Broker payment
        if (requiresPayment(action.catalogue)) {
          let newState = getStateWithStepEnabled(OrderPageName.PAY, false, applicationNew);

          newState = getStateWithStepEnabled(OrderPageName.PAY_ONLINE, true, newState);
          newState = getStateWithStepEnabled(OrderPageName.PAY_STATUS, true, newState);

          applicationNew.steps = [...(newState.steps || [])];
        }

        if (!requiresPayment(action.catalogue)) {
          applicationNew = getStateWithStepEnabled(OrderPageName.PAY, true, applicationNew);
          applicationNew = getStateWithStepEnabled(OrderPageName.PAY_ONLINE, false, applicationNew);
          applicationNew = getStateWithStepEnabled(OrderPageName.PAY_STATUS, false, applicationNew);

          applicationNew.steps = [...(applicationNew.steps || [])];
        }
      }

      if (!!applicationNew.brokerLogin || applicationNew.agentJourney) {
        const newState = getStateWithStepEnabled(
          OrderPageName.ASSIGN_DEVICES,
          true,
          applicationNew,
        );
        applicationNew.steps = [...(newState.steps || [])];
      }

      const solutions = applicationNew.opportunity?.solutions || [];
      if (applicationNew.eCommerceRequired) {
        const isPaymentGatewaySelected: boolean = !Environment.current.disabledFeatures.dpo
          ? (applicationNew.cart ?? []).some(
              (cartItem) => !!PRODUCT_CATEGORY_NAME_MAP[cartItem.productCategoryName ?? ''],
            )
          : !!solutions.find((solution) => {
              const catalogueItem = getCatalogueItemFromCatalogue(
                action.catalogue,
                solution.productId,
                solution.acquiringChannel,
              );
              return !!PRODUCT_CATEGORY_NAME_MAP[catalogueItem?.device?.productCategoryName ?? ''];
            });
        applicationNew.eCommerceRequired = isPaymentGatewaySelected;

        applicationNew = getStateWithStepEnabled(
          Environment.current.disabledFeatures.revamp
            ? OrderPageName.PAYMENT_TYPE
            : OrderPageName.PAYMENT_TYPE_REVAMP,
          isPaymentGatewaySelected,
          applicationNew,
        );
      }

      if (isECommerceItemInCart(applicationNew.cart || [])) {
        applicationNew.eCommerceRequired = true;
        applicationNew = getStateWithStepEnabled(
          Environment.current.disabledFeatures.revamp
            ? OrderPageName.PAYMENT_TYPE
            : OrderPageName.PAYMENT_TYPE_REVAMP,
          true,
          applicationNew,
        );
      }

      if (!Environment.current?.disabledFeatures?.creditVetting) {
        const isCreditVettingEnabled: boolean = !Environment.current.disabledFeatures.dpo
          ? (applicationNew.cart ?? []).some(
              (cartItem) =>
                cartItem.paymentMethodCode === PaymentMethodCode.RENTAL ||
                cartItem.acquiringChannel === AcquiringChannel.rentOnlineFicaOnline,
            )
          : !!solutions.find((solution) => {
              const catalogueItem = getCatalogueItemFromCatalogue(
                action.catalogue,
                solution.productId,
                solution.acquiringChannel,
              );
              return (
                catalogueItem?.device?.acquiringChannel === AcquiringChannel.rentOnlineFicaOnline
              );
            });

        if (applicationNew?.creditVetting) {
          applicationNew.creditVetting.required = isCreditVettingEnabled;
        }

        applicationNew = getStateWithStepEnabled(
          OnboardingPageName.RES_ADDRESS,
          isCreditVettingEnabled,
          applicationNew,
        );
      }

      const isSolePropNotSelected =
        applicationNew.businessDetails?.companyType !== CompanyType.SOLE_PROP;

      applicationNew = getStateWithStepEnabled(
        OnboardingPageName.DIRECTOR_DETAILS,
        isSolePropNotSelected,
        { ...applicationNew, currentStepIndex: action.application.currentStepIndex },
      );

      if (applicationNew.pendingApp?.isPending) {
        applicationNew = getStateWithPendingAppSteps(applicationNew);
      }

      const selectablePaymentMethods = getSelectablePaymentMethods(
        applicationNew,
        action.catalogue,
        'SET_WHOLE_APP',
      );

      const isThirdParty = !!applicationNew.brokerLogin || applicationNew.agentJourney;

      if (isThirdParty) {
        const totalCollectionsQuantity = (applicationNew.collections ?? []).reduce(
          (total, collection) => total + collection.quantity,
          0,
        );

        const cartWithoutVirtualProds = (applicationNew.cart ?? []).filter(
          (cartItem) => !cartItem.isVirtual,
        );
        const totalCartQuantity = calculateTotalCartQuantity(cartWithoutVirtualProds);

        // If all items in the cart are collections then we can disable cash option
        if (totalCartQuantity === totalCollectionsQuantity) {
          const updatedSelectablePaymentMethods = selectablePaymentMethods
            .filter((paymentMethod) => paymentMethod.paymentMethodCode === PaymentMethodCode.CASH)
            .map((paymentMethod) => {
              if (paymentMethod.paymentMethodCode === PaymentMethodCode.CASH) {
                return {
                  ...paymentMethod,
                  disabled: true,
                };
              }
              return paymentMethod;
            });

          applicationNew.selectablePaymentMethodCodes = updatedSelectablePaymentMethods;
        }
      }

      // Set number of tries and max number of tries when continuing with OTP
      if (!isThirdParty && applicationNew?.gatewayPayment && applicationNew?.payment) {
        const currNumOfTries = applicationNew.payment?.orderNumber
          ? Number(applicationNew.payment.orderNumber.slice(-2))
          : undefined;

        applicationNew.gatewayPayment.numberOfTries = currNumOfTries;
        applicationNew.payment.maxNumberOfTries = getMaximumPaymentNumberOfTries(
          currNumOfTries ?? 0,
        );
      }

      return { ...applicationNew, selectablePaymentMethodCodes: selectablePaymentMethods };

    case ApplicationActionType.UPDATE_CARD_FEES:
      const cardFees = !Environment.current.disabledFeatures.dpo
        ? getFeesDPO(state.cart ?? [], action.cardFeesDTO)
        : getFees(action.products, action.cardFeesDTO);

      if (cardFees) {
        const fees = cardFees.map(getCardFee);
        const pricingTier = cardFees[0]?.pricing_tier_name;

        const application: Application = {
          ...state,
          opportunity: {
            ...state.opportunity,
            cardFees: { fees, pricingTier },
          },
        };
        return application;
      }

      return state;

    case ApplicationActionType.UPDATE_SALES_DETAILS:
      return {
        ...state,
        salesDetails: {
          organisation: action.organisation,
          agent: action.agent,
        },
      };

    case ApplicationActionType.SET_GATEWAY_PAYMENT_STATUS: {
      let newState: Application = {
        ...state,
        gatewayPayment: {
          ...state.gatewayPayment,
          initiated: action.initiated,
          transactionId: action.transactionId,
          numberOfTries: action.numberOfTries,
        },
      };

      newState = getStateWithStepEnabled(OrderPageName.PROCESS_PAYMENT, true, newState);
      newState = getStateWithStepEnabled(OrderPageName.PAY_STATUS, true, newState);

      return newState;
    }

    case ApplicationActionType.UPDATE_GATEWAY_PAYMENT_STATUS: {
      const app: Application = {
        ...state,
        gatewayPayment: {
          ...state.gatewayPayment,
          transactionId: action.transactionId,
          responseCode: action.responseCode,
          responseMessage: action.responseMessage,
          numberOfTries: action.isSuccessful
            ? state.gatewayPayment?.numberOfTries || 0
            : (state.gatewayPayment?.numberOfTries || 0) + 1,
        },
      };
      return app;
    }

    case ApplicationActionType.RESET_GATEWAY_PAYMENT_STATUS: {
      let newState: Application = {
        ...state,
        gatewayPayment: {
          ...state.gatewayPayment,
          initiated: false,
          transactionId: '',
          responseCode: '',
          responseMessage: '',
        },
      };

      newState = getStateWithStepEnabled(OrderPageName.PROCESS_PAYMENT, false, newState);
      newState = getStateWithStepEnabled(OrderPageName.PAY_STATUS, false, newState);
      return newState;
    }

    case ApplicationActionType.UPDATE_REMOTE_PAYMENT_STATUS:
      return {
        ...state,
        remotePayment: {
          ...action,
          numberOfTries: action.isNew
            ? (state.remotePayment?.numberOfTries || 0) + 1
            : state.remotePayment?.numberOfTries || 0,
        },
      } as Application;

    case ApplicationActionType.RESET_REMOTE_PAYMENT_STATUS:
      return {
        ...state,
        remotePayment: {
          initiated: false,
          transactionId: '',
          responseCode: '',
          responseMessage: '',
          numberOfTries: state.remotePayment?.numberOfTries,
        },
      } as Application;

    case ApplicationActionType.ATTACH_SERIAL_NUMBER:
      const updatedSolutions = state.opportunity?.solutions?.map((solution) => {
        const serialNumberAdded = (solution.serialNumbers ?? []).some((serialNumber) =>
          action.serialNumber.includes(serialNumber),
        );
        if (
          solution.productId === action.productId &&
          solution.quantity > 0 &&
          !serialNumberAdded
        ) {
          return {
            ...solution,
            serialNumbers: [...(solution.serialNumbers || []), ...action.serialNumber],
            ...(action.friendlyTerminalName && {
              friendlyTerminalName: action.friendlyTerminalName,
            }),
          };
        }

        return solution;
      });

      return {
        ...state,
        opportunity: {
          ...state.opportunity,
          solutions: updatedSolutions,
          deliveries: getDeliveriesAdjustedToMatchTotalSolutionQuantities(
            updatedSolutions || [],
            state.opportunity?.deliveries || [],
          ),
        },
      } as Application;

    case ApplicationActionType.INIT_PAYMENT_METHOD_CODE: {
      const actionCatalogueItem = getCatalogueItemFromCatalogue(
        action.catalogue,
        action.productId,
        action.acquiringChannel,
      );

      if (!actionCatalogueItem) {
        throw new Error(
          `App reducer revamp(INIT_PAYMENT_METHOD_CODE): unknown product: product id: ${action.productId}`,
        );
      }

      // Update the cartItem to the correct paymentMethodCode (DPO)
      const updatedCart = (state.cart || []).map((cartItem) => {
        if (
          cartItem.productId === action.productId &&
          (cartItem.paymentMethodCode === null || cartItem.paymentMethodCode === undefined)
        ) {
          return {
            ...cartItem,
            paymentMethodCode: action.paymentMethodCode
              ? action.paymentMethodCode
              : setPaymentMethod(actionCatalogueItem.device.allowedPaymentMethods),
          };
        }
        return cartItem;
      });

      const solutionsUpdated = (state.opportunity?.solutions || []).map((solution) => {
        if (
          solution.productId === action.productId &&
          solution.acquiringChannel === action.acquiringChannel
        ) {
          return { ...solution, paymentMethodCode: action.paymentMethodCode };
        }
        return solution;
      });

      if (!Environment.current.disabledFeatures.dpo) {
        return {
          ...state,
          cart: updatedCart,
          opportunity: {
            ...state.opportunity,
            solutions: solutionsUpdated,
          },
        };
      }

      const newState: Application = {
        ...state,
        opportunity: {
          ...state.opportunity,
          solutions: solutionsUpdated,
        },
      };

      return newState;
    }

    case ApplicationActionType.ADD_PAYMENT_METHOD_CODE: {
      if (!Environment.current.disabledFeatures.dpo) {
        const updatedCart = (action.cart || []).map((cartItem) => {
          if (action.cartId == cartItem.id) {
            return {
              ...cartItem,
              paymentMethodCode: action.paymentMethodCode,
            };
          }
          return cartItem;
        });

        const newState: Application = {
          ...state,
          cart: updatedCart,
        };
        return newState;
      }

      if (action.catalogue && action.productId && action.acquiringChannel) {
        const actionCatalogueItem = getCatalogueItemFromCatalogue(
          action.catalogue,
          action.productId,
          action.acquiringChannel,
        );

        if (!actionCatalogueItem) {
          throw new Error(
            `App reducer: ADD_PAYMENT_METHOD_CODE: unknown product id: ${action.productId}`,
          );
        }

        const solutionNew: Solution = {
          productId: action.productId,
          acquiringChannel: action.acquiringChannel,
          quantity: actionCatalogueItem.device.quantity,
          paymentMethodCode: action.paymentMethodCode,
        };

        const solutionsUpdated = (state.opportunity?.solutions || []).map((solution) => {
          if (
            solution.productId === action.productId &&
            solution.acquiringChannel === action.acquiringChannel
          ) {
            return solutionNew;
          }
          return solution;
        });

        const newState: Application = {
          ...state,
          opportunity: {
            ...state.opportunity,
            solutions: solutionsUpdated,
          },
        };

        return newState;
      }

      return state;
    }

    case ApplicationActionType.SET_SELECTABLE_PAYMENT_CODES: {
      const selectablePaymentMethods = getSelectablePaymentMethods(
        state,
        action.catalogue,
        'SET_SELECTABLE_PAYMENT_CODES',
      );

      return {
        ...state,
        selectablePaymentMethodCodes: selectablePaymentMethods,
      };
    }

    case ApplicationActionType.UPDATE_SELECTABLE_PAYMENT_CODES: {
      const totalCollectionsQuantity = (action.collections ?? []).reduce(
        (total, collection) => total + collection.quantity,
        0,
      );
      const totalDeliveriesQuantity = (action.deliveries ?? []).reduce(
        (total, collection) => total + collection.quantity,
        0,
      );
      const cartWithoutVirtualProds = (state.cart ?? []).filter((cartItem) => !cartItem.isVirtual);
      const totalCartQuantity = calculateTotalCartQuantity(cartWithoutVirtualProds);

      // If all items in the cart are collections then we can disable collections
      if (totalCartQuantity === totalCollectionsQuantity) {
        const updatedSelectablePaymentMethods = (state.selectablePaymentMethodCodes ?? [])
          .filter((paymentMethod) => paymentMethod.paymentMethodCode === PaymentMethodCode.CASH)
          .map((paymentMethod) => {
            if (paymentMethod.paymentMethodCode === PaymentMethodCode.CASH) {
              return {
                ...paymentMethod,
                disabled: true,
              };
            }
            return paymentMethod;
          });

        return {
          ...state,
          selectablePaymentMethodCodes: updatedSelectablePaymentMethods,
        };
      }

      // If all items in the cart are deliveries then we need the original payment methods
      // This is needed if user switches all collections to deliveries.
      if (totalCartQuantity === totalDeliveriesQuantity && action.catalogue) {
        const updatedSelectablePaymentMethods = getSelectablePaymentMethods(
          state,
          action.catalogue,
          'UPDATE_SELECTABLE_PAYMENT_CODES',
        );

        return {
          ...state,
          selectablePaymentMethodCodes: updatedSelectablePaymentMethods,
        };
      }

      return state;
    }

    case ApplicationActionType.UPDATE_SELECTED_PAYMENT_METHOD: {
      const app: Application = {
        ...state,
        selectedPaymentMethodCode: action.paymentMethodCode,
      };

      return app;
    }

    case ApplicationActionType.SHOW_DEVICE_ACTIVATION: {
      const hasOnlyVirtualDevices = action.productItems.every((item) =>
        isVirtualDevice(item.device.title),
      );

      if (!!state.brokerLogin) {
        return getStateAfterRemovingAssignDevice(
          OrderPageName.ASSIGN_DEVICES,
          !hasOnlyVirtualDevices,
          state,
        );
      }
      return state;
    }

    case ApplicationActionType.NORMALISE_ADDRESSES: {
      // If an item in the cart needs payment and remote payments is disabled, it can only be collected
      const disableDeliveries =
        (state.cart ?? []).some((cartItem) => cartItem.priceType === PriceType.ONCE_OFF) &&
        !state.remotePaymentsEnabled;

      // If we have an item in the cart that can only be delivered
      const disableCollections = state.remotePaymentsEnabled
        ? state.cart?.some(
            (cartItem) =>
              cartItem.deliveryOptions.length === 1 &&
              cartItem.deliveryOptions.includes(DeliveryOptions.Deliver),
          )
        : false;

      const totalCollectionsQuantity = (state.collections ?? []).reduce(
        (total, collection) => total + collection.quantity,
        0,
      );

      if (!action.normalise) {
        return state;
      }

      if (disableDeliveries) {
        const onceOffQuantity = (state.cart ?? []).reduce((total, cartItem) => {
          if (cartItem.priceType === PriceType.ONCE_OFF) {
            return total + cartItem.quantity;
          }
          return total;
        }, 0);

        const newCollections =
          state.remotePaymentsEnabled || onceOffQuantity === 1
            ? [...(state.collections ?? []), ...(state.deliveries as Collection[])]
            : (state.collections ?? []).map((collection) => {
                const deliveryItem = (state.deliveries ?? []).find(
                  (delivery) =>
                    delivery.addressId === collection.addressId &&
                    delivery.productId === collection.productId,
                );
                const cartItem = (state.cart ?? []).find(
                  (item) => item.id == deliveryItem?.cartItemId,
                );

                if (deliveryItem && cartItem && cartItem.priceType === PriceType.ONCE_OFF) {
                  return {
                    ...collection,
                    quantity:
                      deliveryItem.quantity > 1
                        ? deliveryItem.quantity
                        : collection.quantity + deliveryItem.quantity,
                  };
                }
                return collection;
              });

        // Remove the delivery(s) that was added to the collection
        const newDeliveries = (state.deliveries ?? []).filter((delivery) => {
          const deliveryToRemove = newCollections.find(
            (collection) =>
              collection.addressId === delivery.addressId &&
              collection.productId === delivery.productId,
          );
          return !deliveryToRemove;
        });

        const app: Application = {
          ...state,
          collections: newCollections,
          deliveries: newDeliveries,
        };
        return app;
      }

      // Transfer all collections to deliveries
      if (disableCollections) {
        const app: Application = {
          ...state,
          deliveries: [
            ...(state.deliveries ?? []),
            ...((state.collections as AppDelivery[]) ?? []),
          ],
          collections: [],
        };
        return app;
      }

      // Cart Items are added to the deliveries by default so if we have collections already
      // we need to transfer those deliveries to the collections
      if (totalCollectionsQuantity > 0) {
        const updatedCollections = (state.collections ?? []).map((collection) => {
          const deliveryQuantity = (state.deliveries ?? []).find(
            (delivery) => delivery.addressId === collection.addressId,
          )?.quantity;

          if (deliveryQuantity) {
            return {
              ...collection,
              quantity:
                deliveryQuantity > 1 ? deliveryQuantity : collection.quantity + deliveryQuantity,
            };
          }

          return collection;
        });
        const app: Application = {
          ...state,
          collections: updatedCollections,
          deliveries: [],
        };
        return app;
      }

      return state;
    }

    case ApplicationActionType.UPDATE_BANK_ACCOUNT_VALIDATION_ATTEMPTS: {
      const app: Application = {
        ...state,
        bankAccountValidation: {
          ...state.bankAccountValidation,
          attempts: action.attempts,
        },
      };

      return app;
    }

    case ApplicationActionType.SET_PAYMENT_TYPE_OPTIONS: {
      const tenderTypes = getTenderTypes(action.catalogue.products);

      if (tenderTypes.length === 0) {
        return state;
      }

      const productCategoriesInCart = (action.cart ?? [])
        .filter((cartItem) => !cartItem.isTenderType && cartItem.isVirtual)
        .map((cartItem) => cartItem.productCategoryName);

      const tenderTypesAllowed = tenderTypes.filter((item) =>
        productCategoriesInCart.includes(item.productCategoryName),
      );

      const standardTenderTypes = tenderTypesAllowed
        .filter(
          (item) =>
            item.title === DEVICE_NAMES.VISA_MASTERCARD ||
            item.title === DEVICE_NAMES.QR_SCAN_TO_PAY,
        )
        .map((item) => item.title);

      const options: { [key: string]: { image: string; name: string }[] } = {
        standardPayments: [],
        instantEFT: [],
        additionalPayments: [],
      };

      switch (true) {
        case standardTenderTypes.includes(DEVICE_NAMES.QR_SCAN_TO_PAY) &&
          standardTenderTypes.includes(DEVICE_NAMES.VISA_MASTERCARD):
          options['standardPayments'].push({
            image: StandardPaymentsIcon,
            name: 'standardPayments',
          });
          break;

        case standardTenderTypes.includes(DEVICE_NAMES.VISA_MASTERCARD):
          options['standardPayments'].push({ image: VISAMasterCardIcon, name: 'standardPayments' });
          break;

        case standardTenderTypes.includes(DEVICE_NAMES.QR_SCAN_TO_PAY):
          options['standardPayments'].push({ image: QRScanToPayIcon, name: 'standardPayments' });
          break;

        default:
          break;
      }

      const hasInstantEft = tenderTypesAllowed.some(
        (item) => item.title === DEVICE_NAMES.INSTANT_EFT,
      );
      if (hasInstantEft) {
        options['instantEFT'].push({ image: OzowMethodIcon, name: 'Ozow' });
      }

      const hasAdditionalPayments = tenderTypesAllowed.some(
        (item) => item.title === DEVICE_NAMES.AMERICAN_EXPRESS_DINERS,
      );

      if (hasAdditionalPayments) {
        options['additionalPayments'].push(
          { image: '', name: 'amex' },
          { image: '', name: 'diners' },
        );
      }

      const app: Application = {
        ...state,
        paymentTypeOptions: options,
      };

      return app;
    }

    default:
      return state;
  }
}
